import { memo, RefObject } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import useStickyRow from "ds/components/Table/hooks/useStickyRow";

import PersonalSettingsSpacesTableBodyRow from "..";
import { selectGroupsMeta } from "../../usePersonalSettingsSpacesGroupsStore/tableMetaSelectors";
import usePersonalSettingsSpacesTableStore from "../../usePersonalSettingsSpacesTableStore";
import usePersonalSettingsSpacesTableGroupsStore from "../../usePersonalSettingsSpacesGroupsStore";
import { selectTableRowByIndex } from "../../usePersonalSettingsSpacesGroupsStore/tableDataSelectors";
import PersonalSettingsSpacesTableGroupRowContent from "./Content";
import { isSpaceActionsGroup } from "../../helpers";

type PersonalSettingsSpacesTableRowGroupProps = {
  rowIndex: number;
  measureRef: (node: HTMLDivElement) => void;
  tableRef?: RefObject<HTMLDivElement | null>;
  isActiveSticky?: boolean;
  nextAfterSticky?: number;
};

const PersonalSettingsSpacesTableRowGroup = ({
  rowIndex,
  measureRef,
  isActiveSticky,
  nextAfterSticky,
  tableRef,
}: PersonalSettingsSpacesTableRowGroupProps) => {
  const { columns } = usePersonalSettingsSpacesTableStore(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
    }))
  );

  const { row } = usePersonalSettingsSpacesTableGroupsStore(
    useShallow((state) => ({
      row: selectTableRowByIndex(state, rowIndex),
    }))
  );

  const { groupMeta } = usePersonalSettingsSpacesTableGroupsStore(
    useShallow((state) => ({
      groupMeta: selectGroupsMeta(state, isSpaceActionsGroup(row) ? row.spaceID : ""),
    }))
  );

  const rowRef = useStickyRow(isActiveSticky, nextAfterSticky, tableRef);
  const keyboardProps = useRowKeyboardNavigation({ isGroup: true });

  if (!isSpaceActionsGroup(row)) {
    return null;
  }

  return (
    <PersonalSettingsSpacesTableBodyRow
      {...keyboardProps}
      dataIndex={rowIndex}
      isGroupOpen={isActiveSticky}
      forwardRef={(node: HTMLDivElement) => {
        if (measureRef) {
          measureRef(node);
        }
        if (rowRef) {
          rowRef.current = node;
        }
      }}
    >
      <PersonalSettingsSpacesTableGroupRowContent
        columns={columns}
        row={row}
        groupMeta={groupMeta}
      />
    </PersonalSettingsSpacesTableBodyRow>
  );
};

export default memo(PersonalSettingsSpacesTableRowGroup);
